import { getSupabase } from '@/lib/supabase';
import { slugify } from '@/utils/slug';

import { ApiError, toApiError } from '../ApiError';

import {
  type FacetMake,
  type Page,
  type VehicleFacets,
  type VehicleListFilters,
  type VehicleListing,
  type VehicleMedia,
  type VehicleSort,
} from './types';

const MEDIA_BUCKET = 'vehicle-media';
const DEFAULT_PAGE_SIZE = 24;

/** Row shape of public.public_vehicle_listings. */
type ListingRow = {
  id: string;
  stock_no: number;
  slug: string;
  availability: VehicleListing['availability'];
  is_featured: boolean;
  published_at: string;
  updated_at: string;
  make: string;
  model: string;
  trim_level: string | null;
  year_of_manufacture: number;
  body_type: VehicleListing['bodyType'];
  mileage_km: number | null;
  engine_cc: number | null;
  fuel_type: VehicleListing['fuelType'];
  transmission: VehicleListing['transmission'];
  drive: VehicleListing['drive'];
  steering: VehicleListing['steering'];
  exterior_colour: string | null;
  interior_colour: string | null;
  seats: number | null;
  doors: number | null;
  features: string[] | null;
  description: string | null;
  source_country: string;
  auction_grade: string | null;
  price_terms: VehicleListing['price']['terms'];
  price_usd: number | string | null;
  price_ugx: number | string | null;
  cif_destination: string | null;
  shipping_estimate_usd: number | string | null;
  estimated_tax_ugx: number | string | null;
  estimated_landed_cost_ugx: number | string | null;
  estimates_as_of: string | null;
  cover_storage_path: string | null;
  cover_alt_text: string | null;
  cover_width: number | null;
  cover_height: number | null;
};

/** Row shape of public.public_vehicle_media. */
type MediaRow = {
  id: string;
  kind: VehicleMedia['kind'];
  storage_path: string | null;
  external_url: string | null;
  alt_text: string | null;
  position: number;
  is_cover: boolean;
  width: number | null;
  height: number | null;
};

/**
 * A slug is lower-case a–z, 0–9 and hyphens, where each hyphen stood for a run of
 * other characters. As an ILIKE pattern each hyphen becomes `%`, so
 * `mercedes-benz` matches "Mercedes-Benz". Anything else returns null, which
 * also keeps the ILIKE wildcards `%` and `_` out of the pattern.
 */
function slugPattern(slug: string): string | null {
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) ? slug.replace(/-/g, '%') : null;
}

const SORTS: Record<VehicleSort, { column: string; ascending: boolean }> = {
  newest: { column: 'published_at', ascending: false },
  price_usd_asc: { column: 'price_usd', ascending: true },
  price_usd_desc: { column: 'price_usd', ascending: false },
  year_desc: { column: 'year_of_manufacture', ascending: false },
  mileage_asc: { column: 'mileage_km', ascending: true },
};

/** PostgREST returns numeric columns as strings to preserve precision. */
function num(value: number | string | null): number | null {
  if (value === null) return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function publicMediaUrl(path: string): string {
  return getSupabase().storage.from(MEDIA_BUCKET).getPublicUrl(path).data.publicUrl;
}

function toListing(row: ListingRow): VehicleListing {
  return {
    id: row.id,
    stockNo: row.stock_no,
    slug: row.slug,
    availability: row.availability,
    isFeatured: row.is_featured,
    publishedAt: row.published_at,
    updatedAt: row.updated_at,
    make: row.make,
    model: row.model,
    trimLevel: row.trim_level,
    year: row.year_of_manufacture,
    bodyType: row.body_type,
    mileageKm: row.mileage_km,
    engineCc: row.engine_cc,
    fuelType: row.fuel_type,
    transmission: row.transmission,
    drive: row.drive,
    steering: row.steering,
    exteriorColour: row.exterior_colour,
    interiorColour: row.interior_colour,
    seats: row.seats,
    doors: row.doors,
    features: row.features ?? [],
    description: row.description,
    sourceCountry: row.source_country,
    auctionGrade: row.auction_grade,
    price: {
      terms: row.price_terms,
      usd: num(row.price_usd),
      ugx: num(row.price_ugx),
      cifDestination: row.cif_destination,
    },
    estimates: {
      shippingUsd: num(row.shipping_estimate_usd),
      taxUgx: num(row.estimated_tax_ugx),
      landedCostUgx: num(row.estimated_landed_cost_ugx),
      asOf: row.estimates_as_of,
    },
    cover: row.cover_storage_path
      ? {
          url: publicMediaUrl(row.cover_storage_path),
          alt: row.cover_alt_text,
          width: row.cover_width,
          height: row.cover_height,
        }
      : null,
  };
}

function toMedia(row: MediaRow): VehicleMedia | null {
  const url = row.storage_path ? publicMediaUrl(row.storage_path) : row.external_url;
  if (!url) return null;
  return {
    id: row.id,
    kind: row.kind,
    url,
    isExternal: row.storage_path === null,
    alt: row.alt_text,
    position: row.position,
    isCover: row.is_cover,
    width: row.width,
    height: row.height,
  };
}

export async function listVehicles(
  filters: VehicleListFilters = {},
): Promise<Page<VehicleListing>> {
  const page = Math.max(1, filters.page ?? 1);
  const pageSize = filters.pageSize ?? DEFAULT_PAGE_SIZE;
  const sort = SORTS[filters.sort ?? 'newest'];
  const from = (page - 1) * pageSize;

  const makePattern = filters.makeSlug ? slugPattern(filters.makeSlug) : undefined;
  const modelPattern = filters.modelSlug ? slugPattern(filters.modelSlug) : undefined;
  // A malformed slug cannot match any car.
  if (makePattern === null || modelPattern === null) {
    return { items: [], total: 0, page, pageSize };
  }

  let query = getSupabase()
    .from('public_vehicle_listings')
    .select('*', { count: 'exact' })
    .order(sort.column, { ascending: sort.ascending, nullsFirst: false })
    .order('stock_no', { ascending: false })
    .range(from, from + pageSize - 1);

  if (makePattern) query = query.ilike('make', makePattern);
  if (modelPattern) query = query.ilike('model', modelPattern);
  if (filters.yearMin !== undefined) query = query.gte('year_of_manufacture', filters.yearMin);
  if (filters.yearMax !== undefined) query = query.lte('year_of_manufacture', filters.yearMax);
  // NULL <= x is NULL in SQL, so cars without a USD price drop out here.
  if (filters.budgetMaxUsd !== undefined) query = query.lte('price_usd', filters.budgetMaxUsd);
  if (filters.fuel) query = query.eq('fuel_type', filters.fuel);
  if (filters.transmission) query = query.eq('transmission', filters.transmission);
  if (filters.country) query = query.eq('source_country', filters.country);
  if (filters.bodyTypes?.length) query = query.in('body_type', [...filters.bodyTypes]);
  if (filters.featuredOnly) query = query.eq('is_featured', true);
  if (filters.excludeId) query = query.neq('id', filters.excludeId);

  try {
    const { data, error, count } = await query.returns<ListingRow[]>();
    if (error) throw error;
    return { items: data.map(toListing), total: count ?? 0, page, pageSize };
  } catch (error) {
    throw toApiError(error);
  }
}

export async function getVehicleBySlug(slug: string): Promise<VehicleListing> {
  let row: ListingRow | null;
  try {
    const { data, error } = await getSupabase()
      .from('public_vehicle_listings')
      .select('*')
      .eq('slug', slug)
      .maybeSingle<ListingRow>();
    if (error) throw error;
    row = data;
  } catch (error) {
    throw toApiError(error);
  }
  if (!row) throw new ApiError('not_found', 'This vehicle is no longer listed.');
  return toListing(row);
}

export async function listVehicleMedia(listingId: string): Promise<VehicleMedia[]> {
  try {
    const { data, error } = await getSupabase()
      .from('public_vehicle_media')
      .select('*')
      .eq('listing_id', listingId)
      .order('position')
      .returns<MediaRow[]>();
    if (error) throw error;
    return data.map(toMedia).filter((m): m is VehicleMedia => m !== null);
  } catch (error) {
    throw toApiError(error);
  }
}

export async function getVehicleFacets(): Promise<VehicleFacets> {
  let rows: { make: string; model: string; source_country: string }[];
  try {
    const { data, error } = await getSupabase()
      .from('public_vehicle_listings')
      .select('make, model, source_country')
      .returns<{ make: string; model: string; source_country: string }[]>();
    if (error) throw error;
    rows = data;
  } catch (error) {
    throw toApiError(error);
  }
  return buildFacets(
    rows.map((r) => ({ make: r.make, model: r.model, country: r.source_country })),
  );
}

export function buildFacets(
  rows: { make: string; model: string; country: string }[],
): VehicleFacets {
  const makes = new Map<string, FacetMake>();
  const countries = new Set<string>();
  for (const row of rows) {
    countries.add(row.country);
    const makeSlug = slugify(row.make);
    const make = makes.get(makeSlug) ?? { name: row.make, slug: makeSlug, models: [] };
    const modelSlug = slugify(row.model);
    if (!make.models.some((m) => m.slug === modelSlug)) {
      make.models.push({ name: row.model, slug: modelSlug });
    }
    makes.set(makeSlug, make);
  }
  const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
  return {
    makes: [...makes.values()].sort(byName).map((m) => ({ ...m, models: m.models.sort(byName) })),
    countries: [...countries].sort((a, b) => a.localeCompare(b)),
  };
}
